import { CpcIcon } from '@/components/icons/CpcIcon';
import { ActionIcon, Menu, Skeleton, Text } from '@mantine/core';
import { IconDotsVertical, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { CareTeamProviderItem } from '../../hooks/useCareTeamManagement';
import { UnifiedList } from '../shared/UnifiedList';
import { IdentityRow } from '../shared/IdentityRow';
import { TeammateAvatar } from '@/components/TeamAvatar';
import { parseProviderRole } from './services';
import { getPrimaryProviderRole } from '@bariendo/lib/profile';

const CL_FILTERS = {
  light: 'invert(73%) sepia(20%) saturate(256%) hue-rotate(158deg) brightness(93%) contrast(91%)', // #9DB4C1
  dark: 'invert(33%) sepia(96%) saturate(299%) hue-rotate(144deg) brightness(90%) contrast(95%)', // #246A7A
};

type UnifiedCareTeamListProps = {
  items: CareTeamProviderItem[];
  onSetConversationLead: (providerRef: any) => Promise<void>;
  onUnsetConversationLead: () => Promise<void>;
  onRemoveMember: (providerRef: any) => Promise<void>;
  isSaving: boolean;
};

export function UnifiedCareTeamList({
  items,
  onSetConversationLead,
  onUnsetConversationLead,
  onRemoveMember,
  isSaving,
}: UnifiedCareTeamListProps) {
  const [menuOpenedId, setMenuOpenedId] = useState<string | null>(null);

  if (items.length === 0) {
    return (
      <Text c="rgba(0, 0, 0, 0.55)" size="sm" ta="center" py="md">
        No care team members
      </Text>
    );
  }

  return (
    <UnifiedList
      items={items}
      renderItem={(item) => {
        if (!item.value) return <Skeleton height={20} w={100} />;
        const providerRole = parseProviderRole(item.providerRole!);

        return (
          <IdentityRow
            leading={<TeammateAvatar name={item.value} role={getPrimaryProviderRole(item.providerRole)} />}
            primary={item.value}
            secondary={providerRole?.display}
            emphasized={item.conversationLead}
            trailing={
              item.conversationLead ? (
                <CpcIcon size={14} filter={CL_FILTERS['dark']} title="Conversation Lead" />
              ) : undefined
            }
          />
        );
      }}
      renderActions={(item) => (
        <Menu
          shadow="lg"
          width="fit-content"
          position="bottom-end"
          radius="lg"
          opened={menuOpenedId === item.id}
          onChange={(opened) => {
            if (item.id) setMenuOpenedId(opened ? item.id : null);
          }}
        >
          <Menu.Target>
            <ActionIcon variant="subtle" size="xs">
              <IconDotsVertical size={14} color="var(--mantine-color-gray-9)" />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown py="sm">
            {item.conversationLead ? (
              <Menu.Item
                leftSection={<CpcIcon size={16} filter={CL_FILTERS['dark']} />}
                onClick={onUnsetConversationLead}
                disabled={isSaving}
              >
                Unassign as Conversation Lead
              </Menu.Item>
            ) : (
              <Menu.Item
                leftSection={<CpcIcon size={16} filter={CL_FILTERS['dark']} />}
                onClick={() => onSetConversationLead(item.providerReference)}
                disabled={isSaving}
              >
                Assign as Conversation Lead
              </Menu.Item>
            )}
            <Menu.Item
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={() => onRemoveMember(item.providerReference)}
              disabled={isSaving || item.conversationLead}
            >
              Remove from care team
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      )}
    />
  );
}
